import Avatar from "@/atoms-react/avatar/Avatar.react";
import { Base, Info } from "@/atoms-react/text/Text.react";
import { EmptyPageCactusAnimation } from "@/components/animations/empty-cactus/index.react";
import { AmountType, type KypayTransaction } from "@/features/kypay/types";
import type { LanguageCode } from "@/features/utils/constants";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { TransactionType } from "@/features/utils/types";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export type TransactionListProps = {
  transactions: KypayTransaction[];
  numberOfTransactions?: number;
  search?: string;
  listType?: "kycoin" | "kypay";
};

export const TransactionsList = ({
  transactions,
  numberOfTransactions,
  search,
  listType
}: TransactionListProps) => {
  const { t } = useTranslation(["wallet"]);
  const { getLanguage } = usei18n();
  if (numberOfTransactions && numberOfTransactions > 0 && transactions) {
    transactions = transactions.slice(0, numberOfTransactions);
  }

  const handleDate = (date: Date) => {
    const startDate = DateTime.fromJSDate(new Date(date)).set({
      year: DateTime.now().get("year")
    });
    const diff = Math.abs(startDate.diffNow().as("day"));
    if (diff <= 2) {
      const dateString = DateTime.fromJSDate(new Date(date))
        .setLocale(getLanguage().toLocaleUpperCase() as LanguageCode)
        .minus({ days: 0 })
        .toRelativeCalendar();
      return dateString !== null
        ? dateString?.charAt(0).toUpperCase() + dateString.slice(1)
        : DateTime.fromJSDate(new Date(date)).toFormat("dd/MM/yyyy");
    } else {
      return DateTime.fromJSDate(new Date(date)).toFormat("dd/MM/yyyy");
    }
  };

  const HandleType = (trans: KypayTransaction) => {
    switch (trans.type) {
      case TransactionType.DEPOSIT:
        return "D";
      case TransactionType.TRANSFER:
        return "T";
      case TransactionType.REFUND:
        return "R";
      case TransactionType.WITHDRAW:
        return getLanguage() === "fr" ? "R" : "W";
      case TransactionType.DEPOSIT_SWAP:
        return "D";
      case TransactionType.WITHDRAW_SWAP:
        return getLanguage() === "fr" ? "R" : "W";
      case TransactionType.WITHDRAW_REFUND:
        return "R";
      default:
        return "";
    }
  };

  const addLineBreak = (str: string) => {
    const stcArr = str.trim().split("\n");
    if (stcArr.length <= 1) return str;
    return str.split("\n").map((subStr) => {
      return (
        <>
          {subStr}
          <br />
        </>
      );
    });
  };

  const HandleTypeName = (trans: KypayTransaction) => {
    switch (trans.type) {
      case TransactionType.DEPOSIT:
        return getLanguage() === "fr" ? "Dépôt" : "Deposit";
      case TransactionType.TRANSFER:
        return getLanguage() === "fr" ? "Transfert" : "Transfer";
      case TransactionType.REFUND:
        return getLanguage() === "fr" ? "Remboursement" : "Refund";
      case TransactionType.WITHDRAW:
        return getLanguage() === "fr" ? "Retrait" : "Withdraw";
      case TransactionType.DEPOSIT_SWAP:
        return getLanguage() === "fr" ? "Dépôt" : "Deposit";
      case TransactionType.WITHDRAW_SWAP:
        return getLanguage() === "fr" ? "Retrait" : "Withdraw";
      case TransactionType.WITHDRAW_REFUND:
        return getLanguage() === "fr" ? "Remboursement" : "Refund";
    }
  };

  const handleAmount = (transaction: KypayTransaction): string => {
    const isLanguageFrench: Boolean = getLanguage() === "fr";
    const formattedNumber: string = new Intl.NumberFormat(
      isLanguageFrench ? "fr-FR" : "en-US"
    ).format(transaction.amount / 100);
    const currency: string =
      transaction.partner_id === 0 || transaction.token_name === "EUROS"
        ? "€"
        : isLanguageFrench
        ? `${transaction.token_name}`
        : `${transaction.token_name} `;

    return isLanguageFrench ? `${formattedNumber} ${currency}` : `${currency}${formattedNumber}`;
  };

  const truncateString = (str, firstCharCount = str.length, endCharCount = 0, dotCount = 3) => {
    if (str.length <= firstCharCount + endCharCount) {
      return str; // No truncation needed
    }

    const firstPortion = str.slice(0, firstCharCount);
    const endPortion = str.slice(-endCharCount);
    const dots = ".".repeat(dotCount);

    return `${firstPortion}${dots}${endPortion}`;
  };

  return (
    <div>
      {transactions?.length === 0 && !search && (
        <div className="flex h-[600px] flex-col justify-center lg:h-[800px]">
          <div className="flex w-full flex-col items-center space-y-4 p-3">
            <EmptyPageCactusAnimation />
            <Info>{t("kypay.info.no_transaction")}</Info>
          </div>
        </div>
      )}
      {transactions?.length === 0 && !!search && (
        <>
          <div className="flex w-full flex-col items-center space-y-4 p-3">
            <Info>{t("kypay.info.not_found")}</Info>
          </div>
        </>
      )}
      <div className="hidden h-max min-h-[400px] min-w-sm space-y-2 md:block xl:min-w-0">
        {transactions
          ?.sort((a: KypayTransaction, b: KypayTransaction) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          })
          .map((trans, index) => (
            <div
              className={twMerge(
                "flex items-center justify-between rounded-none rounded-sm border-0 border-2 border-slate-50 px-3 py-2 py-3 lg:px-8 xl:w-full dark:border-slate-700",
                index % 2 &&
                  `${
                    listType === "kycoin"
                      ? "bg-orange-50 dark:bg-orange-900"
                      : "bg-blue-50 dark:bg-slate-700"
                  }`
              )}
              key={index}
            >
              <div
                className="flex w-[100px] w-full basis-1/5 items-center lg:min-w-[200px]"
                // @POTENTIAL SIDE EFFECT
                style={{ maxWidth: "calc(100% - 218px)" }}
              >
                <Avatar className="mr-4 shrink-0" size={10} fallback={HandleType(trans)} />
                <div>
                  <Base className="font-semibold text-sm">{HandleTypeName(trans)}</Base>
                </div>
              </div>
              <div className="w-full basis-1/5 py-4 lg:min-w-[200px]">
                <Base className="w-[150px] font-semibold text-sm">{handleDate(trans.date)}</Base>
              </div>
              <div className="w-full basis-1/5 break-all py-4 lg:min-w-[200px]">
                {!trans.cryptoInformations ? (
                  <Base className="pr-5 font-semibold text-sm">
                    {" "}
                    {addLineBreak(getLanguage() === "fr" ? trans.from : trans.from_en)}
                  </Base>
                ) : (
                  <Base
                    className="pr-5 font-semibold text-sm"
                    data-tooltip={getLanguage() === "fr" ? trans.from : trans.from_en}
                    data-position="bottom"
                  >
                    {" "}
                    {getLanguage() === "fr"
                      ? truncateString(trans.from, 5, 5, 3)
                      : truncateString(trans.from_en, 5, 5, 3)}
                  </Base>
                )}
              </div>
              <div className="w-full basis-1/5 break-all py-4 lg:min-w-[200px]">
                {!trans.cryptoInformations ? (
                  <Base className="pr-5 font-semibold text-sm">
                    {" "}
                    {addLineBreak(getLanguage() === "fr" ? trans.to : trans.to_en)}
                  </Base>
                ) : (
                  <Base
                    className="pr-5 font-semibold text-sm"
                    data-tooltip={getLanguage() === "fr" ? trans.to : trans.to_en}
                    data-position="bottom"
                  >
                    {" "}
                    {getLanguage() === "fr"
                      ? truncateString(trans.to, 5, 5, 3)
                      : truncateString(trans.to_en, 5, 5, 3)}
                  </Base>
                )}
              </div>
              <div
                className={twMerge(
                  "w-full basis-1/5 py-4 text-left md:text-center lg:min-w-[200px] xl:text-right"
                )}
              >
                {trans.cryptoInformations ? (
                  <div className={twMerge("w-full xl:w-[200px]")}>
                    <div className="flex flex-row justify-end">
                      <div className="flex flex-col">
                        <Base
                          className={twMerge(
                            "font-semibold text-base",
                            trans.amountType === AmountType.GAIN ? "text-success_kypay" : "",
                            trans.amountType === AmountType.LOSS ? "text-red-600" : "",
                            trans.amountType === AmountType.NEUTRAL ? "text-black" : ""
                          )}
                        >
                          {trans.amountType === AmountType.GAIN ? (
                            <span>+</span>
                          ) : trans.amountType === AmountType.LOSS ? (
                            <span>-</span>
                          ) : (
                            <span> </span>
                          )}
                          {`${trans.cryptoInformations.fromAmount} ${trans.cryptoInformations.fromAsset}`}
                        </Base>
                        <Base
                          className={twMerge(
                            "text-sm",
                            trans.amountType === AmountType.GAIN ? "text-success_kypay" : "",
                            trans.amountType === AmountType.LOSS ? "text-red-600" : "",
                            trans.amountType === AmountType.NEUTRAL ? "text-black" : ""
                          )}
                        >
                          {trans.amountType === AmountType.GAIN ? (
                            <span>+</span>
                          ) : trans.amountType === AmountType.LOSS ? (
                            <span>-</span>
                          ) : (
                            <span> </span>
                          )}
                          {handleAmount(trans)}
                        </Base>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Base
                    className={twMerge(
                      "w-full text-base xl:w-[200px]",
                      trans.amountType === AmountType.GAIN ? "text-success_kypay" : "",
                      trans.amountType === AmountType.LOSS ? "text-red-600" : "",
                      trans.amountType === AmountType.NEUTRAL ? "text-black" : ""
                    )}
                  >
                    {trans.amountType === AmountType.GAIN ? (
                      <span>+</span>
                    ) : trans.amountType === AmountType.LOSS ? (
                      <span>-</span>
                    ) : (
                      <span> </span>
                    )}

                    {handleAmount(trans)}
                  </Base>
                )}
              </div>
            </div>
          ))}
      </div>

      <div className="block min-w-sm border-2 border-slate-50 md:hidden xl:min-w-0 dark:border-slate-700">
        {transactions
          ?.sort((a: KypayTransaction, b: KypayTransaction) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          })
          .map((trans, index) => (
            <div
              className={twMerge(
                "flex h-auto flex-col p-5",
                index % 2 && `bg-blue-50 dark:bg-slate-700`
              )}
            >
              <div className="flex flex-row justify-between">
                <div className="flex justify-center">
                  <Avatar className="mr-4 shrink-0" size={10} fallback={HandleType(trans)} />
                  <div className="flex flex-col justify-center">
                    <Base className="font-semibold text-base">{HandleTypeName(trans)}</Base>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  {trans.cryptoInformations ? (
                    <div className="flex flex-col">
                      <Base
                        className={twMerge(
                          "font-semibold text-base",
                          trans.amountType === AmountType.GAIN ? "text-success_kypay" : "",
                          trans.amountType === AmountType.LOSS ? "text-red-600" : "",
                          trans.amountType === AmountType.NEUTRAL ? "text-black" : ""
                        )}
                      >
                        {trans.amountType === AmountType.GAIN ? (
                          <span>+</span>
                        ) : trans.amountType === AmountType.LOSS ? (
                          <span>-</span>
                        ) : (
                          <span> </span>
                        )}
                        {`${trans.cryptoInformations.fromAmount} ${trans.cryptoInformations.fromAsset}`}
                      </Base>
                      <Base
                        className={twMerge(
                          "text-right text-sm",
                          trans.amountType === AmountType.GAIN ? "text-success_kypay" : "",
                          trans.amountType === AmountType.LOSS ? "text-red-600" : "",
                          trans.amountType === AmountType.NEUTRAL ? "text-black" : ""
                        )}
                      >
                        {trans.amountType === AmountType.GAIN ? (
                          <span>+</span>
                        ) : trans.amountType === AmountType.LOSS ? (
                          <span>-</span>
                        ) : (
                          <span> </span>
                        )}
                        {handleAmount(trans)}
                      </Base>
                    </div>
                  ) : (
                    <Base
                      className={twMerge(
                        trans.amountType === AmountType.GAIN ? "text-success_kypay" : "",
                        trans.amountType === AmountType.LOSS ? "text-red-600" : "",
                        trans.amountType === AmountType.NEUTRAL ? "ml-4 text-black" : ""
                      )}
                    >
                      {` ${
                        trans.amountType === AmountType.GAIN
                          ? "+"
                          : trans.amountType === AmountType.LOSS
                          ? "-"
                          : ""
                      } ${handleAmount(trans)}`}
                    </Base>
                  )}
                </div>
              </div>
              <div className="mt-4 ml-14">
                <Base className="text-sm">{handleDate(trans.date)}</Base>
              </div>
              <div className="mt-4 ml-14">
                <Base className={"text-slate-300 text-sm dark:text-slate-300"}>
                  {t("atoms:molecules.coin_layout.header_from")}
                </Base>
              </div>
              <div className="ml-14 flex flex-wrap">
                <Base className="text-sm">
                  {" "}
                  {addLineBreak(getLanguage() === "fr" ? trans.from : trans.from_en)}
                </Base>
              </div>
              <div className="mt-4 ml-14">
                <Base className={"text-slate-300 text-sm dark:text-slate-300"}>
                  {t("atoms:molecules.coin_layout.header_to")}
                </Base>
              </div>
              <div className="ml-14 flex flex-wrap">
                <Base className="text-sm">
                  {" "}
                  {addLineBreak(getLanguage() === "fr" ? trans.to : trans.to_en)}
                </Base>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
